import React from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { ServiceData, SlotService } from '../../../../utils/state/types';
import { getServiceSlotIdentifier, mapToArray } from '../../../../utils';
import { ReservedPaymentOptionIds } from '../../../../types/types';
import { DynamicPriceErrorType, FormError } from '../../../../types/errors';
import { FormStatus } from '../../../../types/form-state';
import { getErrorByType } from '../../../../utils/errors/errors';
import { useFormActions } from '../../Hooks/useFormActions';
import PriceOptionDropdown, {
  PriceChoice,
} from '../PriceOptionDropdownContainer/PriceOptionDropdown/PriceOptionDropdown';

export interface ParticipantVariantPickerProps {
  serviceData: ServiceData;
  participantIndex: number;
  status: FormStatus;
  errors: FormError[];
}

const ParticipantVariantPicker: React.FC<ParticipantVariantPickerProps> = ({
  serviceData,
  participantIndex,
  status,
  errors,
}) => {
  const { t } = useTranslation();
  const { onParticipantVariantSelect, clearErrorByTypes } = useFormActions();

  const slotService = mapToArray<SlotService>(serviceData.slotServices)[0];
  const { dynamicPriceInfo } = slotService;
  const customOption = dynamicPriceInfo?.customOptions?.[0];

  if (!customOption) {
    return null;
  }

  const serviceSlotId = getServiceSlotIdentifier(slotService.nestedSlot);
  const currentChoice =
    slotService.participantVariants?.[participantIndex]?.choices?.[0];

  const isCurrentChoice = (option: PriceChoice) =>
    !!currentChoice &&
    (option.choiceId === currentChoice.custom ||
      (!!option.id && option.id === currentChoice.id));
  const options: PriceChoice[] = customOption.options.map((option) => ({
    ...option,
    numberOfParticipantsInitialValue: isCurrentChoice(option) ? 1 : 0,
  }));

  const dynamicPriceError = getErrorByType({
    errors,
    errorType: DynamicPriceErrorType,
  });

  return (
    <PriceOptionDropdown
      key={`${serviceSlotId}-p${participantIndex}-${
        currentChoice?.custom ?? 'empty'
      }`}
      componentKey={`${serviceSlotId}-p${participantIndex}`}
      options={options}
      maxParticipants={1}
      showPrice={
        slotService.selectedPaymentOption.id ===
        ReservedPaymentOptionIds.SingleSession
      }
      label={t('app.booking-details.price-option.dropdown-label', {
        serviceName: slotService.service.name,
        optionName: customOption.name,
      })}
      disabled={status === FormStatus.PROCESSING_PAYMENT_DETAILS}
      error={!!dynamicPriceError}
      errorMessage={dynamicPriceError?.params?.message}
      onDropdownClose={(priceOptionData) => {
        onParticipantVariantSelect(
          priceOptionData,
          customOption.optionId,
          serviceSlotId,
          participantIndex,
        );
        clearErrorByTypes([DynamicPriceErrorType]);
      }}
    />
  );
};

export default ParticipantVariantPicker;
